// src/components/invoices/InvoiceWidget.tsx
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react'
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LuReceiptEuro, LuTextSelect } from 'react-icons/lu'

import InvoiceCard from './InvoiceCard'
import InvoiceDetails from './InvoiceDetails'
import InvoiceCardSkeleton from './InvoiceCardSkeleton'
import InvoiceDetailsSkeleton from './InvoiceDetailsSkeleton'
import type { Invoice } from './invoice.types'

import { getBankById, getInvoiceSettings, getBanks } from '@/api/settings.api'
import type { InvoiceSettingsData } from '@/api/dto/settings.dto'
import * as invoicesApi from '@/api/invoices.api'
import CreateInvoiceDialog from './CreateInvoiceDialog'
import {
  formatEntityLabel,
  invoiceDownloadFilename,
  isCreditNote,
  lastDayOfMonth,
  matchesInvoiceCategoryTab,
  matchesInvoiceStatusTab,
  resolveInvoiceLineCustomerName,
  translateInvoiceErrorMessage,
  validateMandatoryInvoiceSettings,
  type UnionAssociatedEntity,
} from '../../helper/invoice.helpers'
import type { Bank } from '@/common/settings.common'
import { toaster } from '../ui/toaster/toasterInstance'
import { CancelInvoiceDialog } from './CancelInvoiceDialog'
import { CancellationCreatedDialog } from './CancellationCreatedDialog'
import { CancellationSentDialog } from './CancellationSentDialog'
// import { RegenerateInvoiceDialog } from './RegenerateInvoiceDialog'

import { WidgetShell } from '@/components/widget-wrappers/WidgetShell'
// import { TabbedSplitLayout } from '@/components/widget-wrappers/TabbedSplitLayout'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import { MobileDrawer } from '@/components/widget-wrappers/MobileDrawer'
import React from 'react'
import { toast } from '../ui/toaster/toasterHelpers'
import { DeleteDraftInvoiceDialog } from './DeleteDraftInvoiceDialog'
import { YearPager } from '@/components/order/YearPager'
import { usePlanningYears } from '@/hooks/usePlanningYears'

type InvoicesApi = {
  getInvoices: typeof invoicesApi.getInvoices
  getInvoiceById: typeof invoicesApi.getInvoiceById
  downloadInvoice: typeof invoicesApi.downloadInvoice
  openInvoicePdf: typeof invoicesApi.openInvoicePdf

  createDraftInvoice: typeof invoicesApi.createDraftInvoice
  finalizeInvoice: typeof invoicesApi.finalizeInvoice
  sendInvoiceById: typeof invoicesApi.sendInvoiceById
  updateInvoiceItems: typeof invoicesApi.updateInvoiceItems
  deleteDraftInvoice: typeof invoicesApi.deleteDraftInvoice
  cancelInvoice: typeof invoicesApi.cancelInvoice
  submitInvoice: typeof invoicesApi.submitInvoice

  // regenerateInvoices: typeof invoicesApi.regenerateInvoices
}

interface InvoiceWidgetProps {
  registrationYear?: number
  onCreateInvoice?: (year: number) => void
  api?: InvoicesApi
}

type InvoiceCategoryTab = 'alle' | 'meine-rechnungen' | 'gutschriften'
type InvoiceStatusTab = 'vorschau' | 'erstellt' | 'versendet' | 'storniert'

// type Locale = 'de' | 'en'
// function getLocale(): Locale {
//   const v = (
//     localStorage.getItem('i18nextLng') ||
//     document.documentElement.lang ||
//     navigator.language ||
//     'de'
//   )?.toLowerCase()
//   return v.startsWith('de') ? 'de' : 'en'
// }
// type Locale = 'de' | 'en'
// function getLocale(): Locale {
//   const v = (
//     localStorage.getItem('i18nextLng') ||
//     document.documentElement.lang ||
//     navigator.language ||
//     'de'
//   )?.toLowerCase()
//   return v.startsWith('de') ? 'de' : 'en'
// }

// function buildPeriod(billingYear: number, billingMonth: number) {
//   const mm = String(billingMonth).padStart(2, '0')
//   const endDay = String(lastDayOfMonth(billingYear, billingMonth)).padStart(2, '0')
//   return {
//     periodStart: `${billingYear}-${mm}-01`,
//     periodEnd: `${billingYear}-${mm}-${endDay}`,
//   }
// }
// function buildPeriod(billingYear: number, billingMonth: number) {
//   const mm = String(billingMonth).padStart(2, '0')
//   const endDay = String(lastDayOfMonth(billingYear, billingMonth)).padStart(2, '0')
//   return {
//     periodStart: `${billingYear}-${mm}-01`,
//     periodEnd: `${billingYear}-${mm}-${endDay}`,
//   }
// }

export type InvoiceWidgetHandle = {
  openCreateInvoice: () => void
}

const InvoiceWidget = React.forwardRef<InvoiceWidgetHandle, InvoiceWidgetProps>(
  ({ registrationYear = 2025, api }, ref) => {
    // ===== Create Invoice Dialog State =====
    const [isCreateDialogOpen, setCreateDialogOpen] = useState(false)
    const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null)
    const openCreateInvoice = () => {
      setEditingInvoiceId(null)
      setCreateDialogOpen(true)
    }
    useImperativeHandle(ref, () => ({
      openCreateInvoice,
    }))

    const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettingsData | null>(null)
    const [loadingInvoiceSettings, setLoadingInvoiceSettings] = useState(false)

    const [sendLoadingId, setSendLoadingId] = useState<string | null>(null)

    const navigate = useNavigate()
    const listScrollRef = useRef<HTMLDivElement | null>(null)
    const [cancelInvoiceId, setCancelInvoiceId] = useState<string | null>(null)

    // ===== Regeneration dialog state (from dev) =====
    // const [isRegenerateOpen, setRegenerateOpen] = useState(false)
    // const [regenerateInvoiceId, setRegenerateInvoiceId] = useState<string | null>(null)
    // const [regenerateLoading, setRegenerateLoading] = useState(false)
    // const [regenerateError, setRegenerateError] = useState<string | null>(null)

    // ===== Year selector =====
    const invoices = api ?? (invoicesApi as unknown as InvoicesApi)
    const currentYear = new Date().getFullYear()
    // The latest year open for planning, as Union set it in the backoffice (SIB-928).
    const { openYears } = usePlanningYears()
    const maxYear = Math.max(currentYear, ...openYears)
    const minYear = useMemo(
      () => Math.min(registrationYear, currentYear),
      [registrationYear, currentYear]
    )

    const [year, setYear] = useState(currentYear)
    // ===== Data state =====
    const [invoicesForYear, setInvoicesForYear] = useState<Invoice[]>([])
    const [loadingList, setLoadingList] = useState(true)
    const [listError, setListError] = useState<string | null>(null)
    const [unionEntities, setUnionEntities] = useState<{ id: number; name: string }[]>([])

    // Selection + details loading
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
    const [loadingDetails, setLoadingDetails] = useState(false)
    const [detailsError, setDetailsError] = useState<string | null>(null)

    // Cancel dialog state
    const [isCancelCreatedOpen, setCancelCreatedOpen] = useState(false)
    const [createdCancellationInvoiceId, setCreatedCancellationInvoiceId] = useState<
      string | null
    >(null)
    const [sendCancelDocLoading, setSendCancelDocLoading] = useState(false)
    const [sendCancelDocError, setSendCancelDocError] = useState<string | null>(null)
    const [isCancelOpen, setCancelOpen] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [lastCancellationReason, setLastCancellationReason] = useState<string>('')
    const [isCancelSentOpen, setCancelSentOpen] = useState(false)
    const [cancelError, setCancelError] = useState<string | null>(null)

    const [isDeleteDraftOpen, setDeleteDraftOpen] = useState(false)
    const [deleteDraftInvoiceId, setDeleteDraftInvoiceId] = useState<string | null>(null)
    const [deleteDraftLoading, setDeleteDraftLoading] = useState(false)
    const [deleteDraftError, setDeleteDraftError] = useState<string | null>(null)

    type Locale = 'de' | 'en'
    function getLocale(): Locale {
      const v = (
        localStorage.getItem('i18nextLng') ||
        document.documentElement.lang ||
        navigator.language ||
        'de'
      )?.toLowerCase()
      return v.startsWith('de') ? 'de' : 'en'
    }

    const locale = useMemo(() => getLocale(), [])

    // ===== Tabs =====
    const [categoryTab, setCategoryTab] = useState<InvoiceCategoryTab>('alle')
    const [statusTab, setStatusTab] = useState<InvoiceStatusTab | null>(null)

    const ERROR_TOAST = {
      de: {
        title: 'Da ist etwas schiefgegangen',
        body:
          'Leider ist bei uns ein Fehler aufgetreten, daher konnten wir die Stornierung nicht durchführen. ' +
          'Bitte versuchen Sie es später erneut. Vielen Dank für Ihr Verständnis.',
      },
      en: {
        title: 'Something went wrong',
        body:
          'Unfortunately, an error occurred and we could not complete the cancellation. ' +
          'Please try again later. Thank you for your understanding.',
      },
    }

    function buildPeriod(billingYear: number, billingMonth: number) {
      const mm = String(billingMonth).padStart(2, '0')
      const endDay = String(lastDayOfMonth(billingYear, billingMonth)).padStart(2, '0')
      return {
        periodStart: `${billingYear}-${mm}-01`,
        periodEnd: `${billingYear}-${mm}-${endDay}`,
      }
    }

    // Helpers for selected invoice scroll into view
    const categoryForInvoice = (inv: Invoice): InvoiceCategoryTab =>
      isCreditNote(inv) ? 'gutschriften' : 'meine-rechnungen'

    const statusForInvoice = (inv: Invoice): InvoiceStatusTab | null => {
      const isUnionFlow = inv.bankId === 1

      if (isUnionFlow) {
        if (inv.status === 'DRAFT' && inv.approvalStatus === 'PENDING_APPROVAL') return 'erstellt'
        if (inv.status === 'DRAFT' && inv.approvalStatus === 'REJECTED') return 'erstellt'
        if (inv.status === 'SENT' && inv.approvalStatus === 'APPROVED') return 'versendet'
      }

      switch (inv.status) {
        case 'DRAFT':
          return 'vorschau'
        case 'FINALIZED':
          return 'erstellt'
        case 'SENT':
          return 'versendet'
        case 'CANCELED':
          return 'storniert'
        default:
          return null
      }
    }
    const scrollCardIntoView = (id: string) => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`invoice-card-${id}`)
        if (!el) return
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    }

    const enrichInvoice = useCallback(
      (inv: Invoice): Invoice => {
        return {
          ...inv,
          lines: (inv.lines ?? []).map(l => ({
            ...l,
            customerName: resolveInvoiceLineCustomerName(l, unionEntities, inv.bankName),
          })),
        }
      },
      [unionEntities]
    )

    const fetchInvoices = async () => {
      setLoadingList(true)
      setListError(null)

      try {
        const yearStart = `${year}-01-01`
        const yearEnd = `${year}-12-31`
        const res = await invoices.getInvoices({ periodStart: yearStart, periodEnd: yearEnd })
        setInvoicesForYear(res.items ?? [])
      } catch (e: any) {
        setListError(e?.message || 'Rechnungen konnten nicht geladen werden.')
        setInvoicesForYear([])
      } finally {
        setLoadingList(false)
      }
    }
    const handleSubmitInvoice = async (invoiceId: string) => {
      setSendLoadingId(invoiceId)
      try {
        const submitted = await invoices.submitInvoice(invoiceId)

        await fetchInvoices()
        setSelectedId(submitted.id)
        setDetailsRefreshKey(k => k + 1)
        await refreshDetails(submitted.id)

        toast.success('Rechnung wurde zur Freigabe eingereicht.')
      } catch (e: any) {
        toast.error(
          'Rechnung konnte nicht eingereicht werden',
          typeof e?.message === 'string' ? e.message : 'Unbekannter Fehler'
        )
      } finally {
        setSendLoadingId(null)
      }
    }
    const handlePreview = async (invoiceId: string) => {
      try {
        await invoices.openInvoicePdf(invoiceId)
      } catch (e: any) {
        toast.error(
          'Vorschau nicht möglich',
          typeof e?.message === 'string'
            ? e.message
            : 'Die PDF-Vorschau konnte nicht geöffnet werden.'
        )
      }
    }

    const handleDeleteDraft = async (invoiceId: string) => {
      try {
        setDeleteDraftLoading(true)
        setDeleteDraftError(null)

        await invoices.deleteDraftInvoice(invoiceId)

        await fetchInvoices()
        setSelectedId(null)
        setSelectedInvoice(null)

        setDeleteDraftOpen(false)
        setDeleteDraftInvoiceId(null)

        toaster.success({ title: 'Entwurf gelöscht' })
      } catch (e: any) {
        const msg = typeof e?.message === 'string' ? e.message : 'Unbekannter Fehler'
        setDeleteDraftError(msg)

        toaster.error({
          title: 'Entwurf konnte nicht gelöscht werden',
          description: msg,
        })
      } finally {
        setDeleteDraftLoading(false)
      }
    }

    const [detailsRefreshKey, setDetailsRefreshKey] = useState(0)

    const refreshDetails = async (id: string) => {
      try {
        setLoadingDetails(true)
        setDetailsError(null)
        const inv = await invoices.getInvoiceById(id)
        setSelectedInvoice(enrichInvoice(inv))
        return inv
      } catch (e: any) {
        setSelectedInvoice(null)
        setDetailsError(e?.message || 'Rechnungsdetails konnten nicht geladen werden.')
      } finally {
        setLoadingDetails(false)
      }
    }

    const handleEditDraft = (invoiceId: string) => {
      setSelectedId(invoiceId)
      setEditingInvoiceId(invoiceId)
      setCreateDialogOpen(true)
    }

    const handleFinalizeDraft = async (invoiceId: string) => {
      setSendLoadingId(invoiceId)
      try {
        const finalized = await invoices.finalizeInvoice(invoiceId)

        await fetchInvoices()
        setSelectedId(finalized.id)
        setDetailsRefreshKey(k => k + 1)
        await refreshDetails(finalized.id)

        toast.success('Rechnung wurde erstellt.')
      } catch (e: any) {
        toast.error(
          'Rechnung konnte nicht erstellt werden.',
          translateInvoiceErrorMessage(e?.message) ??
            (typeof e?.message === 'string' ? e.message : 'Unbekannter Fehler')
        )
      } finally {
        setSendLoadingId(null)
      }
    }
    // Load union entities
    useEffect(() => {
      let mounted = true
      ;(async () => {
        try {
          const res = await getBanks()
          const list = res.items ?? []
          const union = list.find((b: any) => (b.name ?? '').toLowerCase().includes('union'))
          if (!union) return

          const details = await getBankById(union.id)
          const assoc: UnionAssociatedEntity[] = (details?.associatedEntities ?? []).map(
            (e: any) => ({
              id: Number(e.id),
              name: String(e.name ?? ''),
              name2: e.name2 ?? null,
              name3: e.name3 ?? null,
            })
          )

          if (mounted) setUnionEntities(assoc)
        } catch {
          if (mounted) setUnionEntities([])
        }
      })()
      return () => {
        mounted = false
      }
    }, [])

    // Load details on selection/year/refreshKey
    useEffect(() => {
      let mounted = true

      async function loadDetails() {
        if (!selectedId) return
        try {
          setLoadingDetails(true)
          setDetailsError(null)
          const inv = await invoices.getInvoiceById(selectedId)
          if (!mounted) return
          setSelectedInvoice(enrichInvoice(inv))
        } catch (e: any) {
          if (!mounted) return
          setSelectedInvoice(null)
          setDetailsError(e?.message || 'Rechnungsdetails konnten nicht geladen werden.')
        } finally {
          if (mounted) setLoadingDetails(false)
        }
      }

      loadDetails()
      return () => {
        mounted = false
      }
    }, [selectedId, year, detailsRefreshKey, invoices, enrichInvoice])

    // Reset selection + reload list on year
    useEffect(() => {
      setSelectedId(null)
      setSelectedInvoice(null)
      setDetailsError(null)

      fetchInvoices()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year])

    const handleSendFinalized = async (invoiceId: string) => {
      try {
        setSendLoadingId(invoiceId)
        const sent = await invoices.sendInvoiceById(invoiceId)
        await fetchInvoices()
        setSelectedId(sent.id)
        setDetailsRefreshKey(k => k + 1)
        await refreshDetails(String(sent.id))
      } finally {
        setSendLoadingId(null)
      }
    }

    // Load invoice settings for defaults
    useEffect(() => {
      let mounted = true

      async function loadInvoiceSettings() {
        setLoadingInvoiceSettings(true)
        try {
          const s = await getInvoiceSettings()
          if (!mounted) return
          setInvoiceSettings(s)
        } catch {
          if (!mounted) return
          setInvoiceSettings(null)
        } finally {
          if (mounted) setLoadingInvoiceSettings(false)
        }
      }

      loadInvoiceSettings()
      return () => {
        mounted = false
      }
    }, [])

    const handleCancelInvoice = async (invoiceId: string, cancellationReason: string) => {
      setCancelLoading(true)
      setCancelError(null)

      try {
        const created = await invoices.cancelInvoice(invoiceId, {
          cancellationReason,
          sendCancellationInvoice: false,
        })

        const newCancellationInvoiceId = created?.cancellationInvoice?.id

        if (!newCancellationInvoiceId || Number.isNaN(Number(newCancellationInvoiceId))) {
          throw new Error('Die ID der Stornorechnung fehlt in der Antwort.')
        }

        setLastCancellationReason(cancellationReason)
        setCreatedCancellationInvoiceId(String(newCancellationInvoiceId))

        await fetchInvoices()
        setDetailsRefreshKey(k => k + 1)
        await refreshDetails(invoiceId)

        setCancelOpen(false)
        setSendCancelDocError(null)
        setCancelCreatedOpen(true)

        toaster.success({ title: 'Rechnung storniert' })
      } catch (e: any) {
        const msg =
          e?.message || (locale === 'de' ? 'Stornierung fehlgeschlagen.' : 'Cancellation failed.')
        setCancelError(msg)

        const t = ERROR_TOAST[locale]
        toaster.error({ title: t.title, description: t.body })
      } finally {
        setCancelLoading(false)
      }
    }

    const handleSendCancellationAndClose = async () => {
      if (!createdCancellationInvoiceId) return

      setSendCancelDocLoading(true)
      setSendCancelDocError(null)

      try {
        await invoices.sendInvoiceById(createdCancellationInvoiceId)

        await fetchInvoices()
        setDetailsRefreshKey(k => k + 1)

        if (cancelInvoiceId) {
          await refreshDetails(cancelInvoiceId)
        }

        setCancelCreatedOpen(false)
        setCancelSentOpen(true)
      } catch (e: any) {
        setSendCancelDocError(
          e?.message || (locale === 'de' ? 'Versand fehlgeschlagen.' : 'Sending failed.')
        )
      } finally {
        setSendCancelDocLoading(false)
      }
    }

    // const handleRegenerateInvoice = async (invoiceId: string) => {
    //   setRegenerateLoading(true)
    //   setRegenerateError(null)

    //   try {
    //     await invoices.regenerateInvoices({
    //       invoiceIds: [Number(invoiceId)],
    //       send: false,
    //     })

    //     await fetchInvoices()
    //     setDetailsRefreshKey(k => k + 1)
    //     await refreshDetails(invoiceId)

    //     setRegenerateOpen(false)
    //     toaster.success({
    //       title: 'Rechnung regeneriert',
    //       description: 'Die Rechnung wurde mit den aktuellen Einstellungen neu berechnet.',
    //     })
    //   } catch (e: any) {
    //     const msg = e?.message || 'Regenerierung fehlgeschlagen.'
    //     setRegenerateError(msg)

    //     toaster.error({
    //       title: 'Regenerierung fehlgeschlagen',
    //       description: msg,
    //     })
    //   } finally {
    //     setRegenerateLoading(false)
    //   }
    // }

    const { invoiceSettingsComplete, invoiceSettingsMissingFields } = useMemo(() => {
      if (loadingInvoiceSettings) {
        return {
          invoiceSettingsComplete: undefined as boolean | undefined,
          invoiceSettingsMissingFields: [] as string[],
        }
      }

      const res = validateMandatoryInvoiceSettings(invoiceSettings)
      return {
        invoiceSettingsComplete: res.complete,
        invoiceSettingsMissingFields: res.missing,
      }
    }, [invoiceSettings, loadingInvoiceSettings])

    // Load banks for CreateInvoiceDialog
    const [banks, setBanks] = useState<Bank[]>([])
    useEffect(() => {
      let mounted = true
      async function loadBanks() {
        try {
          const res = await getBanks({ parentId: 0 })
          if (!mounted) return
          setBanks(res.items ?? [])
        } catch {
          if (!mounted) return
          setBanks([])
        }
      }
      loadBanks()
      return () => {
        mounted = false
      }
    }, [])

    const bankOptions = useMemo(
      () =>
        banks.map(b => ({
          label: formatEntityLabel(b.name, b.name2, b.name3),
          value: String(b.id),
        })),
      [banks]
    )

    // keep selection visible
    useEffect(() => {
      if (!selectedId) return
      scrollCardIntoView(selectedId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, categoryTab])

    const filteredInvoices = useMemo(() => {
      return invoicesForYear.filter(
        invoice =>
          matchesInvoiceCategoryTab(invoice, categoryTab) &&
          matchesInvoiceStatusTab(invoice, statusTab)
      )
    }, [invoicesForYear, categoryTab, statusTab])
    const categoryCounts = useMemo(() => {
      const all = invoicesForYear
      return {
        alle: all.length,
        'meine-rechnungen': all.filter(i => !isCreditNote(i)).length,
        gutschriften: all.filter(i => isCreditNote(i)).length,
      }
    }, [invoicesForYear])

    const statusCounts = useMemo(() => {
      return {
        vorschau: invoicesForYear.filter(i => statusForInvoice(i) === 'vorschau').length,
        erstellt: invoicesForYear.filter(i => statusForInvoice(i) === 'erstellt').length,
        versendet: invoicesForYear.filter(i => statusForInvoice(i) === 'versendet').length,
        storniert: invoicesForYear.filter(i => statusForInvoice(i) === 'storniert').length,
      }
    }, [invoicesForYear])

    const handleDownload = async (type: 'pdf' | 'xml' | 'zip') => {
      if (!selectedInvoice) return
      const name = invoiceDownloadFilename(
        selectedInvoice.invoiceNumber || `invoice-${selectedInvoice.id}`,
        type
      )
      await invoices.downloadInvoice(selectedInvoice.id, type, name)
    }

    // ===== Responsive layout behavior (from current branch) =====
    const hasAnyInvoices = invoicesForYear.length > 0
    const [isMobile] = useMediaQuery(['(max-width: 767px)'])
    const [isDrawerOpen, setDrawerOpen] = useState(false)

    const onSelect = (id: string) => {
      setSelectedId(id)
      if (isMobile) setDrawerOpen(true)
    }

    // ===== Left content =====
    const leftDetailsContent = loadingList ? (
      <VStack align="stretch" gap="stack">
        <InvoiceCardSkeleton />
        <InvoiceCardSkeleton />
        <InvoiceCardSkeleton />
      </VStack>
    ) : listError ? (
      <EmptyStateCard
        icon={<LuTextSelect size={36} />}
        title="Fehler beim Laden"
        description={listError}
        action={
          <Button variant="outline" onClick={fetchInvoices}>
            Erneut versuchen
          </Button>
        }
      />
    ) : filteredInvoices.length === 0 ? (
      <EmptyStateCard
        icon={<LuTextSelect size={36} />}
        title={hasAnyInvoices ? 'Keine Treffer' : 'Keine Rechnungen'}
        description={
          hasAnyInvoices
            ? 'Für diesen Filter gibt es aktuell keine Rechnungen.'
            : 'Für das ausgewählte Kalenderjahr liegen keine Rechnungen vor.'
        }
      />
    ) : (
      <VStack align="stretch" gap="stack">
        {filteredInvoices.map(inv => (
          <Box key={inv.id} id={`invoice-card-${inv.id}`}>
            <InvoiceCard
              invoice={inv}
              unionEntities={unionEntities}
              isActive={inv.id === selectedId}
              onClick={() => onSelect(inv.id)}
            />
          </Box>
        ))}
      </VStack>
    )

    // ===== Right content =====
    const rightDetailsContent = loadingDetails ? (
      <InvoiceDetailsSkeleton />
    ) : detailsError ? (
      <EmptyStateCard
        icon={<LuTextSelect size={36} />}
        title="Fehler beim Laden"
        description={detailsError}
      />
    ) : !selectedInvoice ? (
      <EmptyStateCard
        icon={<LuTextSelect size={36} />}
        title={hasAnyInvoices ? 'Keine Rechnung ausgewählt' : 'Keine Rechnungen'}
        description={
          hasAnyInvoices
            ? 'Wählen Sie eine Rechnung aus der Liste aus.'
            : 'Für das ausgewählte Kalenderjahr liegen keine Rechnungen vor.'
        }
      />
    ) : (
      <InvoiceDetails
        invoice={selectedInvoice}
        unionEntities={unionEntities}
        onDownload={handleDownload}
        onPreview={() => handlePreview(selectedInvoice.id)}
        onDeleteDraft={() => {
          setDeleteDraftError(null)
          setDeleteDraftInvoiceId(selectedInvoice.id)
          setDeleteDraftOpen(true)
        }}
        onEditDraft={() => handleEditDraft(selectedInvoice.id)}
        onSubmitForApproval={() => handleSubmitInvoice(selectedInvoice.id)}
        onFinalizeDraft={() => handleFinalizeDraft(selectedInvoice.id)}
        onSendFinalized={() => handleSendFinalized(selectedInvoice.id)}
        onCancel={() => {
          setCancelError(null)
          setLastCancellationReason('')
          setCancelOpen(true)
          setCancelInvoiceId(selectedInvoice.id)
        }}
        isSending={sendLoadingId === selectedInvoice.id}
        onDownloadCancellation={type => {
          const cancelId = selectedInvoice.cancellationInvoiceId
          if (!cancelId) return
          const name = invoiceDownloadFilename(`Storno-${selectedInvoice.invoiceNumber}`, type)
          return invoices.downloadInvoice(cancelId, type, name)
        }}
        onOpenInvoice={id => {
          const target = invoicesForYear.find(x => String(x.id) === String(id))

          if (target) {
            setCategoryTab(categoryForInvoice(target))
            setStatusTab(statusForInvoice(target))
          } else {
            setCategoryTab('alle')
            setStatusTab(null)
          }

          setSelectedId(String(id))
          setDetailsRefreshKey(k => k + 1)
          scrollCardIntoView(String(id))
          if (isMobile) setDrawerOpen(true)
        }}
        // onRegenerate={() => {
        //   if (!selectedInvoice) return
        //   const isActivity =
        //     (selectedInvoice.scope ?? selectedInvoice.type ?? '').toString().toUpperCase() ===
        //     'ACTIVITY'
        //   const okStatus =
        //     selectedInvoice.status === 'DRAFT' || selectedInvoice.status === 'FINALIZED'
        //   if (!isActivity || !okStatus) return

        //   setRegenerateError(null)
        //   setRegenerateInvoiceId(selectedInvoice.id)
        //   setRegenerateOpen(true)
        // }}
      />
    )

    const rightDesktopNode = (
      <Card.Root
        flex="1"
        borderColor="bg.muted"
        minH={0}
        maxH={{ base: '320px', md: '560px' }}
        overflowY="auto"
      >
        <Card.Body>{rightDetailsContent}</Card.Body>
      </Card.Root>
    )

    const leftDesktopNode = (
      <Card.Root flex="1" minH={0} borderColor="border" overflowY="auto">
        <Card.Body ref={listScrollRef} maxH={{ base: '320px', md: '560px' }}>
          {leftDetailsContent}
        </Card.Body>
      </Card.Root>
    )

    const rightDrawerNode = <Flex h="full">{rightDetailsContent}</Flex>

    return (
      <WidgetShell
        title="Rechnungen"
        headerAfterTitle={
          <HStack
            width="full"
            gap={4}
            justifyContent={{ base: 'space-between', md: 'flex-start' }}
          ></HStack>
        }
        headerRight={
          <Button
            id="new-invoice-button"
            onClick={openCreateInvoice}
            variant="outline"
            data-testid="invoice-new-button"
            // colorPalette="grey"
          >
            <LuReceiptEuro />
            Rechnung erstellen
          </Button>
        }
      >
        <VStack align="stretch" gap={4} h={{ base: '420px', md: '620px' }}>
          <HStack gap={2} wrap="wrap" justifyContent="space-between">
            <HStack gap={2} wrap="wrap">
              <Button
                data-testid="invoice-category-alle"
                variant={categoryTab === 'alle' ? 'subtle' : 'ghost'}
                onClick={() => {
                  setCategoryTab('alle')
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Alle <Badge>{categoryCounts.alle}</Badge>
              </Button>
              <Button
                data-testid="invoice-category-meine-rechnungen"
                variant={categoryTab === 'meine-rechnungen' ? 'subtle' : 'ghost'}
                onClick={() => {
                  setCategoryTab('meine-rechnungen')
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Meine Rechnungen <Badge>{categoryCounts['meine-rechnungen']}</Badge>
              </Button>

              <Button
                data-testid="invoice-category-gutschriften"
                variant={categoryTab === 'gutschriften' ? 'subtle' : 'ghost'}
                onClick={() => {
                  setCategoryTab('gutschriften')
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Gutschriften <Badge>{categoryCounts.gutschriften}</Badge>
              </Button>

              <Button
                data-testid="invoice-status-vorschau"
                variant={statusTab === 'vorschau' ? 'subtle' : 'ghost'}
                onClick={() => {
                  setStatusTab(prev => (prev === 'vorschau' ? null : 'vorschau'))
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Vorschau <Badge>{statusCounts.vorschau}</Badge>
              </Button>

              <Button
                data-testid="invoice-status-erstellt"
                variant={statusTab === 'erstellt' ? 'ghost' : 'subtle'}
                onClick={() => {
                  setStatusTab(prev => (prev === 'erstellt' ? null : 'erstellt'))
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Erstellt <Badge>{statusCounts.erstellt}</Badge>
              </Button>
              <Button
                data-testid="invoice-status-versendet"
                variant={statusTab === 'versendet' ? 'ghost' : 'subtle'}
                onClick={() => {
                  setStatusTab(prev => (prev === 'versendet' ? null : 'versendet'))
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Versendet <Badge>{statusCounts.versendet}</Badge>
              </Button>
              <Button
                data-testid="invoice-status-storniert"
                variant={statusTab === 'storniert' ? 'ghost' : 'subtle'}
                onClick={() => {
                  setStatusTab(prev => (prev === 'storniert' ? null : 'storniert'))
                  setSelectedId(null)
                  setSelectedInvoice(null)
                  setDetailsError(null)
                  if (isMobile) setDrawerOpen(false)
                }}
              >
                Storniert <Badge>{statusCounts.storniert}</Badge>
              </Button>
            </HStack>

            <YearPager
              year={year}
              minYear={minYear}
              maxYear={maxYear}
              onChange={setYear}
              testIdPrefix="invoice-year"
            />
          </HStack>

          {!hasAnyInvoices ? (
            <Box flex="1" minH={0}>
              <EmptyStateCard
                icon={<LuTextSelect size={36} />}
                title="Keine Rechnung vorhanden"
                description="Für den ausgewählten Zeitraum liegt keine Rechnung vor."
                action={
                  <Button onClick={openCreateInvoice} colorPalette="gray">
                    <LuReceiptEuro />
                    Neue Rechnung erstellen
                  </Button>
                }
              />
            </Box>
          ) : (
            <Flex gap={2} minH={0} flex="1" direction={{ base: 'column', md: 'row' }}>
              <Box flex="1" minH={0}>
                {leftDesktopNode}
              </Box>
              {!isMobile && (
                <Box flex="1" minH={0}>
                  {rightDesktopNode}
                </Box>
              )}
            </Flex>
          )}
        </VStack>

        {/* Mobile Drawer */}
        {isMobile && (
          <MobileDrawer
            open={isDrawerOpen}
            onClose={() => setDrawerOpen(false)}
            variant="full"
            header={
              <Box>
                <Heading size="xl">Rechnung</Heading>
              </Box>
            }
          >
            {rightDrawerNode}
          </MobileDrawer>
        )}

        {/* ===== Create Invoice Dialog ===== */}
        <CreateInvoiceDialog
          isOpen={isCreateDialogOpen}
          onOpenChange={open => {
            setCreateDialogOpen(open)
            if (!open) setEditingInvoiceId(null)
          }}
          mode={editingInvoiceId ? 'edit' : 'create'}
          draftInvoiceId={editingInvoiceId}
          draftInvoice={editingInvoiceId ? selectedInvoice : null}
          autoInvoiceEnabled={
            loadingInvoiceSettings ? false : !!invoiceSettings?.autoGenerationEnabled
          }
          autoInvoiceDayOfMonth={invoiceSettings?.autoGenerationDay ?? undefined}
          onGoToInvoices={() => setCategoryTab('alle')}
          onDownloadCreatedInvoice={async invoiceId => {
            const inv = await invoices.getInvoiceById(String(invoiceId))
            const name = invoiceDownloadFilename(
              inv.invoiceNumber || `invoice-${invoiceId}`,
              'pdf'
            )
            await invoices.downloadInvoice(String(invoiceId), 'pdf', name)
          }}
          defaultInvoicePrefix={invoiceSettings?.activityInvoicePrefix ?? undefined}
          defaultOfferPrefix={invoiceSettings?.offerNumberPrefix ?? undefined}
          banks={bankOptions}
          onChanged={async id => {
            await fetchInvoices()
            if (id) {
              setSelectedId(String(id))
              setDetailsRefreshKey(k => k + 1)
              await refreshDetails(String(id))
              if (isMobile) setDrawerOpen(true)
            }
          }}
          onSaveDraft={async payload => {
            const { periodStart, periodEnd } = buildPeriod(
              payload.billingYear as any,
              payload.billingMonth as any
            )

            const res = await invoices.createDraftInvoice({
              scope: 'ACTIVITY',
              bankId: payload.bankId,
              periodStart,
              periodEnd,
              itemsIds: [],
            })

            await fetchInvoices()
            setSelectedId(String(res.id))
            if (isMobile) setDrawerOpen(true)
            return { ok: true, invoiceId: String(res.id) }
          }}
          onCreateInvoice={async payload => {
            const { periodStart, periodEnd } = buildPeriod(
              payload.billingYear as any,
              payload.billingMonth as any
            )

            const draft = await invoices.createDraftInvoice({
              scope: 'ACTIVITY',
              bankId: payload.bankId,
              periodStart,
              periodEnd,
              itemsIds: [],
            })

            const finalized = await invoices.finalizeInvoice(draft.id)

            await fetchInvoices()
            setSelectedId(finalized.id)
            if (isMobile) setDrawerOpen(true)
            return { ok: true, invoiceId: finalized.id }
          }}
          onSendInvoice={async payload => {
            const { periodStart, periodEnd } = buildPeriod(
              payload.billingYear as any,
              payload.billingMonth as any
            )

            const draft = await invoices.createDraftInvoice({
              scope: 'ACTIVITY',
              bankId: payload.bankId,
              periodStart,
              periodEnd,
              itemsIds: [],
            })

            const finalized = await invoices.finalizeInvoice(draft.id)
            const sent = await invoices.sendInvoiceById(finalized.id)

            await fetchInvoices()
            setSelectedId(sent.id)
            if (isMobile) setDrawerOpen(true)
            return { ok: true, invoiceId: sent.id }
          }}
          invoiceSettingsComplete={invoiceSettingsComplete}
          invoiceSettingsMissingFields={invoiceSettingsMissingFields}
          onGoToInvoiceSettings={() => navigate('/settings/invoice')}
        />

        <CancelInvoiceDialog
          isOpen={isCancelOpen}
          onClose={() => {
            if (!cancelLoading) setCancelOpen(false)
          }}
          loading={cancelLoading}
          error={cancelError}
          onConfirm={cancellationReason => {
            if (!cancelInvoiceId) return
            return handleCancelInvoice(cancelInvoiceId, cancellationReason)
          }}
        />

        <CancellationCreatedDialog
          isOpen={isCancelCreatedOpen}
          onClose={() => {
            if (!sendCancelDocLoading) {
              setCancelCreatedOpen(false)
              setCreatedCancellationInvoiceId(null)
              setLastCancellationReason('')
              setSendCancelDocError(null)
            }
          }}
          onSendAndClose={handleSendCancellationAndClose}
          loading={sendCancelDocLoading}
          error={sendCancelDocError}
        />

        <CancellationSentDialog
          isOpen={isCancelSentOpen}
          onClose={() => {
            setCancelSentOpen(false)
            setCreatedCancellationInvoiceId(null)
            setLastCancellationReason('')
          }}
        />
        <DeleteDraftInvoiceDialog
          isOpen={isDeleteDraftOpen}
          onClose={() => {
            if (!deleteDraftLoading) {
              setDeleteDraftOpen(false)
              setDeleteDraftInvoiceId(null)
              setDeleteDraftError(null)
            }
          }}
          loading={deleteDraftLoading}
          error={deleteDraftError}
          onConfirm={() => {
            if (!deleteDraftInvoiceId) return
            return handleDeleteDraft(deleteDraftInvoiceId)
          }}
        />

        {/* <RegenerateInvoiceDialog
          isOpen={isRegenerateOpen}
          onClose={() => {
            if (!regenerateLoading) setRegenerateOpen(false)
          }}
          loading={regenerateLoading}
          error={regenerateError}
          onConfirm={() => {
            if (!regenerateInvoiceId) return
            return handleRegenerateInvoice(regenerateInvoiceId)
          }}
        /> */}
      </WidgetShell>
    )
  }
)

InvoiceWidget.displayName = 'InvoiceWidget'
export default InvoiceWidget
